import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth.jsx";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Forgot from "@/pages/Forgot";
import Reset from "@/pages/Reset";
import AcceptInvite from "@/pages/AcceptInvite";
import Onboarding from "@/pages/Onboarding";
import AppShell from "@/pages/AppShell";

// Marketing pages are code-split so the auth bundle stays lean.
const Landing = lazy(() => import("@/pages/marketing/Landing"));
const ProductPage = lazy(() => import("@/pages/marketing/ProductPage"));
const PricingPage = lazy(() => import("@/pages/marketing/PricingPage"));
const ContactPage = lazy(() => import("@/pages/marketing/ContactPage"));
const LegalPage = lazy(() => import("@/pages/marketing/LegalPage"));

function Protected({ children }) {
  const { user, currentOrg, booted } = useAuth();
  const location = useLocation();
  if (!booted) return <div className="p-6 text-sm text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!currentOrg) return <Navigate to="/onboarding" replace />;
  if (!currentOrg.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return children;
}

// PublicOnly is used for auth screens (sign in / sign up) — an authed user should
// go straight to their workspace. Marketing pages remain accessible to everyone.
function PublicOnly({ children }) {
  const { user, currentOrg, booted } = useAuth();
  if (!booted) return <div className="p-6 text-sm text-zinc-500">Loading…</div>;
  if (user && currentOrg) return <Navigate to="/app/office" replace />;
  return children;
}

function MarketingFallback() {
  return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-400">Loading…</div>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<MarketingFallback />}>
          <Routes>
            {/* Marketing (public) */}
            <Route path="/" element={<Landing />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />

            {/* Auth */}
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/signup" element={<PublicOnly><Register /></PublicOnly>} />
            {/* Back-compat alias */}
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/reset" element={<Reset />} />

            {/* Invitation */}
            <Route path="/invite/:token" element={<AcceptInvite />} />

            {/* App */}
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/app/*" element={<Protected><AppShell /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
